import React, { Suspense, lazy, useState } from "react";
import { AnalyticsProvider, useAnalyticsContext } from "../../context/AnalyticsContext";
import { getShortRedirectUrl } from "../../services/api";

const UrlClicksChart = lazy(() => import("./UrlClicksChart"));

function formatDate(value: string): string {
  return new Date(value).toLocaleString("de-DE", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function isValidHttpUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

function copyTextFallback(text: string): boolean {
  if (typeof document === "undefined") return false;

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  textarea.style.pointerEvents = "none";
  document.body.appendChild(textarea);
  textarea.select();
  textarea.setSelectionRange(0, text.length);

  let copied = false;
  try {
    copied = document.execCommand("copy");
  } catch {
    copied = false;
  } finally {
    document.body.removeChild(textarea);
  }

  return copied;
}

function DashboardContent() {
  const {
    loading,
    refreshing,
    error,
    selectedUrl,
    selectedUrlClicks,
    selectedUrlId,
    loadingSelectedDetails,
    detailsError,
    searchQuery,
    sortKey,
    sortDirection,
    statusFilter,
    page,
    pageSize,
    totalPages,
    totalResults,
    paginatedUrls,
    autoRefreshEnabled,
    statsLoading,
    statsError,
    statsConsistencyMismatch,
    totalUrls,
    totalClicks,
    activeUrls,
    creatingUrl,
    createUrlError,
    createUrlSuccess,
    deletingUrlId,
    deleteUrlError,
    setSearchQuery,
    setSortKey,
    toggleSortDirection,
    setStatusFilter,
    setPage,
    setPageSize,
    setAutoRefreshEnabled,
    selectUrl,
    refreshUrls,
    refreshSelectedUrlClicks,
    createNewUrl,
    removeUrl,
    clearMutationMessages,
    exportFilteredAsCsv,
  } = useAnalyticsContext();
  const [newOriginalUrl, setNewOriginalUrl] = useState("");
  const [newCustomCode, setNewCustomCode] = useState("");
  const [createFormError, setCreateFormError] = useState<string | null>(null);
  const [copiedUrlId, setCopiedUrlId] = useState<number | null>(null);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);

  async function handleCopyShortUrl(urlId: number, shortUrl: string) {
    const shortLink = getShortRedirectUrl(shortUrl);

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shortLink);
      } else if (!copyTextFallback(shortLink)) {
        throw new Error("Clipboard API nicht verfügbar.");
      }
      setCopiedUrlId(urlId);
      setCopyFeedback("Kurzlink wurde in die Zwischenablage kopiert.");
      window.setTimeout(() => setCopiedUrlId(null), 1800);
      window.setTimeout(() => setCopyFeedback(null), 2500);
    } catch {
      setCopiedUrlId(null);
      setCopyFeedback("Kopieren nicht möglich. Bitte Link manuell kopieren.");
    }
  }

  async function handleCreateUrlSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (creatingUrl) return;

    const originalUrl = newOriginalUrl.trim();
    const customCode = newCustomCode.trim();

    if (!originalUrl) {
      setCreateFormError("Bitte eine Ziel-URL eingeben.");
      return;
    }

    if (!isValidHttpUrl(originalUrl)) {
      setCreateFormError("Bitte eine gültige URL mit http:// oder https:// eingeben.");
      return;
    }

    if (customCode && !/^[a-zA-Z0-9_-]{3,50}$/.test(customCode)) {
      setCreateFormError("Custom-Code muss 3-50 Zeichen enthalten (Buchstaben, Zahlen, _ oder -).");
      return;
    }

    setCreateFormError(null);
    const created = await createNewUrl({
      original_url: originalUrl,
      custom_short_url: customCode || undefined,
    });

    if (created) {
      setNewOriginalUrl("");
      setNewCustomCode("");
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 px-4 pt-12 pb-16">
        <div className="mx-auto max-w-7xl rounded-2xl border border-gray-200 bg-white p-10 text-center shadow-lg">
          <p className="text-lg font-semibold text-gray-900">Analytics werden geladen...</p>
          <p className="mt-2 text-sm text-gray-600">Bitte kurz warten.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 px-4 pt-12 pb-16">
        <div className="mx-auto max-w-3xl rounded-2xl border border-red-200 bg-white p-8 shadow-lg">
          <h1 className="text-2xl font-bold text-red-700">Analytics konnten nicht geladen werden</h1>
          <p className="mt-2 text-sm text-red-700">{error}</p>
          <button
            type="button"
            onClick={() => {
              void refreshUrls();
            }}
            className="mt-6 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 px-5 py-3 font-medium text-white hover:from-blue-700 hover:to-purple-700"
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 px-4 pt-8 pb-12">
      <div className="mx-auto max-w-7xl space-y-6">
        <section className="rounded-3xl border border-indigo-100 bg-white/90 p-6 shadow-xl backdrop-blur-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Analytics Dashboard</h1>
              <p className="mt-2 text-sm text-gray-600">
                Überblick über URLs, Klickzahlen, Trends und Export.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  void refreshUrls();
                }}
                className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Aktualisieren
              </button>
              <button
                type="button"
                onClick={exportFilteredAsCsv}
                className="rounded-xl bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-black"
              >
                CSV exportieren
              </button>
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-gray-300 px-3 py-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={autoRefreshEnabled}
                  onChange={(event) => setAutoRefreshEnabled(event.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Auto-Refresh (30s)
              </label>
            </div>
          </div>
          {refreshing && (
            <p className="mt-3 text-xs font-medium text-blue-700">Live-Update wird ausgeführt...</p>
          )}
        </section>

        <section className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <p className="text-sm text-gray-600">Gesamt-URLs</p>
            <p className="mt-2 text-3xl font-bold text-gray-900">
              {statsLoading ? "..." : totalUrls}
            </p>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <p className="text-sm text-gray-600">Gesamt-Klicks</p>
            <p className="mt-2 text-3xl font-bold text-gray-900">
              {statsLoading ? "..." : totalClicks}
            </p>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <p className="text-sm text-gray-600">Aktive URLs</p>
            <p className="mt-2 text-3xl font-bold text-gray-900">
              {statsLoading ? "..." : activeUrls}
            </p>
          </div>
        </section>

        {statsError && (
          <section className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Backend-Statistiken konnten nicht geladen werden. Es werden berechnete Werte aus der URL-Liste angezeigt.
          </section>
        )}

        {statsConsistencyMismatch && (
          <section className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
            Hinweis: Aggregierte Backend-Statistiken können kurzfristig von Listensummen abweichen (Eventual Consistency).
          </section>
        )}

        {copyFeedback && (
          <section className="rounded-2xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-800">
            {copyFeedback}
          </section>
        )}

        <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900">Neue URL erstellen</h2>
          <p className="mt-1 text-sm text-gray-600">
            Erstelle direkt im Dashboard neue Kurzlinks.
          </p>

          <form onSubmit={handleCreateUrlSubmit} className="mt-4 grid gap-3 md:grid-cols-[1.7fr_1fr_auto]">
            <input
              type="url"
              value={newOriginalUrl}
              onChange={(event) => {
                setNewOriginalUrl(event.target.value);
                setCreateFormError(null);
                clearMutationMessages();
              }}
              placeholder="https://example.com/very/long/link"
              className="rounded-xl border border-gray-300 px-4 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            />
            <input
              type="text"
              value={newCustomCode}
              onChange={(event) => {
                setNewCustomCode(event.target.value);
                setCreateFormError(null);
                clearMutationMessages();
              }}
              placeholder="Optionaler Custom-Code"
              className="rounded-xl border border-gray-300 px-4 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            />
            <button
              type="submit"
              disabled={creatingUrl}
              className="rounded-xl bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
            >
              {creatingUrl ? "Erstelle..." : "Kurzlink erstellen"}
            </button>
          </form>

          {createFormError && (
            <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {createFormError}
            </div>
          )}

          {createUrlError && (
            <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {createUrlError}
            </div>
          )}

          {createUrlSuccess && (
            <div className="mt-3 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
              {createUrlSuccess}
            </div>
          )}
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
            <input
              type="search"
              value={searchQuery}
              onChange={(event) => {
                setSearchQuery(event.target.value);
                setPage(1);
              }}
              placeholder="Suche nach Kurzlink oder Ziel-URL"
              className="rounded-xl border border-gray-300 px-4 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 xl:col-span-2"
            />

            <select
              value={sortKey}
              onChange={(event) => setSortKey(event.target.value as typeof sortKey)}
              className="rounded-xl border border-gray-300 px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            >
              <option value="created_at">Sortierung: Erstelldatum</option>
              <option value="click_count">Sortierung: Klickzahl</option>
              <option value="short_url">Sortierung: Kurzlink</option>
            </select>

            <button
              type="button"
              onClick={toggleSortDirection}
              className="rounded-xl border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Richtung: {sortDirection === "asc" ? "Aufsteigend" : "Absteigend"}
            </button>

            <select
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value as typeof statusFilter);
                setPage(1);
              }}
              className="rounded-xl border border-gray-300 px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            >
              <option value="all">Status: Alle</option>
              <option value="active">Status: Aktiv</option>
              <option value="inactive">Status: Inaktiv</option>
            </select>
          </div>
        </section>

        <section className="grid gap-6 lg:grid-cols-[1.1fr_1fr]">
          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">URL-Liste</h2>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>{totalResults} Treffer</span>
                <select
                  value={pageSize}
                  onChange={(event) => {
                    setPageSize(Number(event.target.value));
                    setPage(1);
                  }}
                  className="rounded-lg border border-gray-300 px-2 py-1 text-sm text-gray-700"
                >
                  <option value={5}>5</option>
                  <option value={8}>8</option>
                  <option value={12}>12</option>
                  <option value={20}>20</option>
                </select>
              </div>
            </div>

            <div className="space-y-2 max-h-96 overflow-y-auto">
              {deleteUrlError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {deleteUrlError}
                </div>
              )}

              {paginatedUrls.length === 0 && (
                <div className="rounded-xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
                  Keine URLs für die aktuelle Filterung gefunden.
                </div>
              )}

              {paginatedUrls.map((url) => (
                <div
                  key={url.id}
                  className={`w-full rounded-xl border p-3 text-left transition-all ${
                    selectedUrlId === url.id
                      ? "border-blue-400 bg-blue-50"
                      : "border-gray-200 hover:border-blue-200 hover:bg-gray-50"
                  }`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => selectUrl(url.id)}
                      className="flex-1 min-w-0 text-left"
                    >
                      <p className="font-semibold text-gray-900 truncate">{url.short_url}</p>
                      <p className="mt-1 truncate text-sm text-gray-600">{url.original_url}</p>
                    </button>
                    <div className="flex items-center justify-between sm:justify-end gap-2">
                      <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">
                        {url.click_count} Klicks
                      </span>
                      <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                        url.is_active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                      }`}>
                        {url.is_active ? "Aktiv" : "Inaktiv"}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          void handleCopyShortUrl(url.id, url.short_url);
                        }}
                        className="rounded-lg border border-blue-300 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50"
                      >
                        {copiedUrlId === url.id ? "Kopiert" : "Kopieren"}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const confirmed = window.confirm(
                            "URL wirklich löschen? Dieser Vorgang kann nicht rückgängig gemacht werden.",
                          );
                          if (!confirmed) return;
                          void removeUrl(url.id);
                        }}
                        disabled={deletingUrlId === url.id}
                        className="rounded-lg border border-red-300 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {deletingUrlId === url.id ? "Lösche..." : "Löschen"}
                      </button>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-gray-500 sm:hidden">
                    {formatDate(url.created_at)}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-3">
              <button
                type="button"
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                className="rounded-lg border border-gray-300 px-3 py-1 text-sm text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Zurück
              </button>
              <p className="text-sm text-gray-600">
                Seite {page} von {totalPages}
              </p>
              <button
                type="button"
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                className="rounded-lg border border-gray-300 px-3 py-1 text-sm text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Weiter
              </button>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            {!selectedUrl && (
              <div className="flex h-full min-h-[280px] items-center justify-center rounded-xl border border-dashed border-gray-300 text-center text-sm text-gray-500">
                Wähle eine URL aus der Liste, um Details und Klickhistorie zu sehen.
              </div>
            )}

            {selectedUrl && (
              <div className="space-y-4">
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-lg font-semibold text-gray-900">{selectedUrl.short_url}</h3>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          void handleCopyShortUrl(selectedUrl.id, selectedUrl.short_url);
                        }}
                        className="rounded-lg border border-blue-300 px-3 py-1 text-sm text-blue-700 hover:bg-white"
                      >
                        {copiedUrlId === selectedUrl.id ? "Kopiert" : "Kurzlink kopieren"}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          void refreshSelectedUrlClicks();
                        }}
                        className="rounded-lg border border-gray-300 px-3 py-1 text-sm text-gray-700 hover:bg-white"
                      >
                        Detaildaten aktualisieren
                      </button>
                    </div>
                  </div>
                  <a
                    href={selectedUrl.original_url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 block break-all text-sm text-blue-700 hover:text-blue-800"
                  >
                    {selectedUrl.original_url}
                  </a>
                  <div className="mt-3 grid gap-2 text-xs text-gray-600 sm:grid-cols-3">
                    <p>Klicks: {selectedUrl.click_count}</p>
                    <p>Status: {selectedUrl.is_active ? "Aktiv" : "Inaktiv"}</p>
                    <p>Erstellt: {formatDate(selectedUrl.created_at)}</p>
                  </div>
                </div>

                {detailsError && (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {detailsError}
                  </div>
                )}

                {loadingSelectedDetails && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
                    Klickhistorie wird geladen...
                  </div>
                )}

                {!loadingSelectedDetails && (
                  <Suspense
                    fallback={
                      <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                        Diagramm wird geladen...
                      </div>
                    }
                  >
                    <UrlClicksChart clicks={selectedUrlClicks} />
                  </Suspense>
                )}

                <div className="rounded-xl border border-gray-200 bg-white p-3">
                  <h4 className="mb-2 text-sm font-semibold text-gray-900">Neueste Klicks</h4>
                  {selectedUrlClicks.length === 0 && (
                    <p className="text-sm text-gray-500">Noch keine Klickdaten vorhanden.</p>
                  )}
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {selectedUrlClicks.slice(0, 10).map((click) => (
                      <div key={click.id} className="rounded-lg border border-gray-100 bg-gray-50 p-2 text-xs">
                        <p className="text-gray-800 font-medium">{formatDate(click.clicked_at)}</p>
                        <p className="truncate text-gray-500 mt-1" title={click.useragent}>
                          {click.useragent || "Unbekannt"}
                        </p>
                      </div>
                    ))}
                  </div>
                  {selectedUrlClicks.length > 10 && (
                    <p className="mt-2 text-xs text-gray-500 text-center">
                      Zeige 10 von {selectedUrlClicks.length} Klicks
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

export default function AnalyticsDashboard() {
  return (
    <AnalyticsProvider>
      <DashboardContent />
    </AnalyticsProvider>
  );
}
